import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type SupportedMime = 'image/jpeg' | 'image/png' | 'image/webp'
type OutputFormat = 'jpg' | 'png' | 'webp'
type WatermarkPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
type WatermarkErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_large_image'
  | 'corrupt_image'
  | 'canvas_error'
  | 'unsupported_output'
  | 'empty_text'
  | 'export_failed'

type WatermarkCopy = Translations['watermarkImage']

interface LoadedImage {
  file: File
  url: string
  width: number
  height: number
  mime: SupportedMime
}

interface WatermarkResult {
  blob: Blob
  mime: SupportedMime
}

interface WatermarkSettings {
  text: string
  position: WatermarkPosition
  opacity: number
  size: number
  rotation: number
  outputFormat: OutputFormat
}

interface TextPlacement {
  x: number
  y: number
  align: CanvasTextAlign
  baseline: CanvasTextBaseline
}

const UTILITY_KEY = 'watermark-image'
const MAX_FILE_SIZE = 50 * 1024 * 1024
const MAX_PIXELS = 36_000_000
const ACCEPTED_TYPES: SupportedMime[] = ['image/jpeg', 'image/png', 'image/webp']
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']
const DEFAULT_SETTINGS: WatermarkSettings = {
  text: 'imgify.ru',
  position: 'bottom-right',
  opacity: 55,
  size: 8,
  rotation: -18,
  outputFormat: 'jpg',
}

let webpEncodeSupport: Promise<boolean> | null = null

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function isSupportedImage(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type as SupportedMime) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

function sourceMime(file: File): SupportedMime {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()

  if (type === 'image/png' || name.endsWith('.png')) return 'image/png'
  if (type === 'image/webp' || name.endsWith('.webp')) return 'image/webp'
  return 'image/jpeg'
}

function outputMime(format: OutputFormat): SupportedMime {
  if (format === 'png') return 'image/png'
  if (format === 'webp') return 'image/webp'
  return 'image/jpeg'
}

function outputExtension(mime: SupportedMime): string {
  if (mime === 'image/png') return 'png'
  if (mime === 'image/webp') return 'webp'
  return 'jpg'
}

function outputFilename(inputName: string, mime: SupportedMime): string {
  const base = inputName.replace(/\.[^.]+$/, '') || 'image'
  return `${base}-watermark.${outputExtension(mime)}`
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

class WatermarkImageError extends Error {
  constructor(
    message: string,
    readonly code: WatermarkErrorCode,
  ) {
    super(message)
  }
}

function validateFile(
  file: File,
  copy: WatermarkCopy,
): { message: string; code: WatermarkErrorCode } | null {
  if (!isSupportedImage(file)) {
    return { message: copy.errorsText.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_FILE_SIZE) {
    return { message: copy.errorsText.tooLarge, code: 'too_large' }
  }
  return null
}

function loadImage(src: string, copy: WatermarkCopy): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () =>
      reject(new WatermarkImageError(copy.errorsText.corrupt, 'corrupt_image'))
    image.src = src
  })
}

function supportsWebpEncode(): Promise<boolean> {
  if (!webpEncodeSupport) {
    webpEncodeSupport = new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      if (!canvas.toBlob) {
        resolve(false)
        return
      }
      canvas.toBlob((blob) => resolve(blob?.type === 'image/webp'), 'image/webp', 0.85)
    })
  }
  return webpEncodeSupport
}

function blobFromCanvas(
  canvas: HTMLCanvasElement,
  mime: SupportedMime,
  copy: WatermarkCopy,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new WatermarkImageError(copy.errorsText.exportFailed, 'export_failed'))
          return
        }
        if (mime === 'image/webp' && blob.type !== 'image/webp') {
          reject(new WatermarkImageError(copy.errorsText.unsupportedOutput, 'unsupported_output'))
          return
        }
        resolve(blob)
      },
      mime,
      mime === 'image/png' ? undefined : 0.92,
    )
  })
}

function textPlacement(
  position: WatermarkPosition,
  width: number,
  height: number,
): TextPlacement {
  const margin = Math.max(18, Math.round(Math.min(width, height) * 0.06))
  const centerX = width / 2
  const centerY = height / 2

  switch (position) {
    case 'top-left':
      return { x: margin, y: margin, align: 'left', baseline: 'top' }
    case 'top-center':
      return { x: centerX, y: margin, align: 'center', baseline: 'top' }
    case 'top-right':
      return { x: width - margin, y: margin, align: 'right', baseline: 'top' }
    case 'center':
      return { x: centerX, y: centerY, align: 'center', baseline: 'middle' }
    case 'bottom-left':
      return { x: margin, y: height - margin, align: 'left', baseline: 'bottom' }
    case 'bottom-center':
      return { x: centerX, y: height - margin, align: 'center', baseline: 'bottom' }
    case 'bottom-right':
    default:
      return { x: width - margin, y: height - margin, align: 'right', baseline: 'bottom' }
  }
}

function drawWatermark(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  settings: WatermarkSettings,
  output: SupportedMime,
): void {
  const width = image.naturalWidth
  const height = image.naturalHeight

  if (output === 'image/jpeg') {
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, width, height)
  }

  context.drawImage(image, 0, 0)

  const text = settings.text.trim()
  const fontSize = Math.max(14, Math.round(Math.min(width, height) * (settings.size / 100)))
  const placement = textPlacement(settings.position, width, height)

  context.save()
  context.translate(placement.x, placement.y)
  context.rotate((settings.rotation * Math.PI) / 180)
  context.globalAlpha = settings.opacity / 100
  context.font = `700 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  context.textAlign = placement.align
  context.textBaseline = placement.baseline
  context.lineJoin = 'round'
  context.strokeStyle = 'rgba(0, 0, 0, 0.38)'
  context.lineWidth = Math.max(2, fontSize * 0.08)
  context.strokeText(text, 0, 0)
  context.fillStyle = '#FFFFFF'
  context.fillText(text, 0, 0)
  context.restore()
}

async function renderWatermark(
  image: LoadedImage,
  settings: WatermarkSettings,
  copy: WatermarkCopy,
): Promise<WatermarkResult> {
  const text = settings.text.trim()
  if (!text) {
    throw new WatermarkImageError(copy.errorsText.emptyText, 'empty_text')
  }

  const mime = outputMime(settings.outputFormat)
  if (mime === 'image/webp' && !(await supportsWebpEncode())) {
    throw new WatermarkImageError(copy.errorsText.unsupportedOutput, 'unsupported_output')
  }

  const decoded = await loadImage(image.url, copy)
  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height

  const context = canvas.getContext('2d')
  if (!context) {
    canvas.width = 0
    canvas.height = 0
    throw new WatermarkImageError(copy.errorsText.canvas, 'canvas_error')
  }

  try {
    drawWatermark(context, decoded, settings, mime)
    const blob = await blobFromCanvas(canvas, mime, copy)
    return { blob, mime }
  } finally {
    canvas.width = 0
    canvas.height = 0
  }
}

export function WatermarkImagePage() {
  const t = useT()
  const copy = t.watermarkImage
  const [image, setImage] = useState<LoadedImage | null>(null)
  const [settings, setSettings] = useState<WatermarkSettings>(DEFAULT_SETTINGS)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const generationRef = useRef(0)
  const operationSequenceRef = useRef(0)
  const activeOperationRef = useRef<number | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
    }
  }, [])

  useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image.url)
      }
    }
  }, [image])

  const invalidateGeneration = useCallback((): number => {
    generationRef.current += 1
    return generationRef.current
  }, [])

  const loadFile = useCallback(
    async (file: File) => {
      setError(null)
      const requestGeneration = invalidateGeneration()

      const validationError = validateFile(file, copy)
      if (validationError) {
        setError(validationError.message)
        toolError(UTILITY_KEY, validationError.code)
        return
      }

      const url = URL.createObjectURL(file)
      try {
        const decoded = await loadImage(url, copy)
        if (requestGeneration !== generationRef.current) {
          URL.revokeObjectURL(url)
          return
        }
        const width = decoded.naturalWidth
        const height = decoded.naturalHeight
        if (width < 1 || height < 1 || width * height > MAX_PIXELS) {
          throw new WatermarkImageError(copy.errorsText.tooLargeImage, 'too_large_image')
        }

        const nextImage: LoadedImage = {
          file,
          url,
          width,
          height,
          mime: sourceMime(file),
        }
        setImage((previous) => {
          if (previous) {
            URL.revokeObjectURL(previous.url)
          }
          return nextImage
        })
        setSettings((current) => ({
          ...current,
          outputFormat:
            sourceMime(file) === 'image/png'
              ? 'png'
              : sourceMime(file) === 'image/webp'
                ? 'webp'
                : 'jpg',
        }))
      } catch (err) {
        URL.revokeObjectURL(url)
        if (requestGeneration !== generationRef.current) {
          return
        }
        const message = err instanceof Error ? err.message : copy.errorsText.corrupt
        const code = err instanceof WatermarkImageError ? err.code : 'corrupt_image'
        setError(message)
        toolError(UTILITY_KEY, code)
      }
    },
    [copy, invalidateGeneration],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void loadFile(file)
      }
    },
    [loadFile],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const updateSettings = useCallback(
    <T extends keyof WatermarkSettings>(key: T, value: WatermarkSettings[T]) => {
      if (Object.is(settings[key], value)) {
        return
      }
      setSettings((current) => ({ ...current, [key]: value }))
      invalidateGeneration()
      setError(null)
    },
    [invalidateGeneration, settings],
  )

  const handleDownload = useCallback(async () => {
    if (!image || activeOperationRef.current !== null) return

    const operationId = ++operationSequenceRef.current
    const operationGeneration = generationRef.current
    const imageSnapshot = image
    const settingsSnapshot = { ...settings }
    activeOperationRef.current = operationId
    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      position: settingsSnapshot.position,
      opacity: settingsSnapshot.opacity,
      size: settingsSnapshot.size,
      rotation: settingsSnapshot.rotation,
      outputFormat: settingsSnapshot.outputFormat,
    })
    setIsProcessing(true)
    setError(null)

    try {
      const nextResult = await renderWatermark(imageSnapshot, settingsSnapshot, copy)
      if (
        !mountedRef.current ||
        activeOperationRef.current !== operationId ||
        generationRef.current !== operationGeneration
      ) {
        return
      }
      downloadBlob(
        nextResult.blob,
        outputFilename(imageSnapshot.file.name, nextResult.mime),
      )
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        position: settingsSnapshot.position,
        opacity: settingsSnapshot.opacity,
        size: settingsSnapshot.size,
        rotation: settingsSnapshot.rotation,
        outputFormat: settingsSnapshot.outputFormat,
        mime: nextResult.mime,
      })
    } catch (err) {
      if (
        !mountedRef.current ||
        activeOperationRef.current !== operationId ||
        generationRef.current !== operationGeneration
      ) {
        return
      }
      const message = err instanceof Error ? err.message : copy.errorsText.exportFailed
      const code = err instanceof WatermarkImageError ? err.code : 'export_failed'
      setError(message)
      toolError(UTILITY_KEY, code, {
        position: settingsSnapshot.position,
        outputFormat: settingsSnapshot.outputFormat,
      })
    } finally {
      if (activeOperationRef.current === operationId) {
        activeOperationRef.current = null
        if (mountedRef.current) {
          setIsProcessing(false)
        }
      }
    }
  }, [copy, image, settings])

  const watermarkPreviewStyle = useMemo(() => {
    const [vertical, horizontal] =
      settings.position === 'center'
        ? ['center', 'center']
        : settings.position.split('-')
    const justify =
      horizontal === 'left' ? 'flex-start' : horizontal === 'right' ? 'flex-end' : 'center'
    const align =
      vertical === 'top' ? 'flex-start' : vertical === 'bottom' ? 'flex-end' : 'center'

    return {
      justifyContent: justify,
      alignItems: align,
    }
  }, [settings.position])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={[copy.formats, t.common.maxFileSizePerFile]}
        collapsed={Boolean(image)}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          image && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              image && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                TXT
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  image && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleInputChange}
              />
              <Button onClick={() => inputRef.current?.click()}>
                {image ? copy.replaceImage : copy.chooseFile}
              </Button>
            </CardContent>
          </Card>

          {image && (
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="grid grid-cols-2 gap-px bg-border">
                  <div className="bg-muted">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span>{copy.sourceLabel}</span>
                      <span>{image.mime.replace('image/', '').toUpperCase()}</span>
                    </div>
                    <div className="aspect-video bg-background/65">
                      <img
                        src={image.url}
                        alt={image.file.name}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </div>
                  <div className="bg-muted">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span>{copy.resultLabel}</span>
                      <span>{settings.outputFormat.toUpperCase()}</span>
                    </div>
                    <div className="relative aspect-video overflow-hidden bg-background/65">
                      <img
                        src={image.url}
                        alt={copy.waitingPreview}
                        className="h-full w-full object-contain"
                      />
                      <div
                        className="pointer-events-none absolute inset-0 flex p-6"
                        style={watermarkPreviewStyle}
                      >
                        <span
                          className="max-w-full break-words rounded-sm px-1 text-center font-bold text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.65)]"
                          style={{
                            opacity: settings.opacity / 100,
                            transform: `rotate(${settings.rotation}deg)`,
                            fontSize: `${Math.max(18, settings.size * 3)}px`,
                          }}
                        >
                          {settings.text.trim() || copy.emptyPreview}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold tracking-normal" title={image.file.name}>
                        {image.file.name}
                      </h2>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                          {formatSize(image.file.size)}
                        </span>
                        <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                          {image.width} x {image.height}
                        </span>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                      {error}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {image && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{copy.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <label className="block space-y-2">
                  <span className="text-sm font-medium">{copy.textLabel}</span>
                  <input
                    type="text"
                    value={settings.text}
                    maxLength={80}
                    onChange={(event) => updateSettings('text', event.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </label>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{copy.positionLabel}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {copy.positions.map((position) => (
                      <Button
                        key={position.key}
                        type="button"
                        variant={settings.position === position.key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateSettings('position', position.key as WatermarkPosition)}
                      >
                        {position.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <label className="block space-y-2">
                  <span className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>{copy.opacityLabel}</span>
                    <span className="tabular-nums text-muted-foreground">{settings.opacity}%</span>
                  </span>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={1}
                    value={settings.opacity}
                    onChange={(event) => updateSettings('opacity', Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </label>

                <label className="block space-y-2">
                  <span className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>{copy.sizeLabel}</span>
                    <span className="tabular-nums text-muted-foreground">{settings.size}%</span>
                  </span>
                  <input
                    type="range"
                    min={4}
                    max={20}
                    step={1}
                    value={settings.size}
                    onChange={(event) => updateSettings('size', Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </label>

                <label className="block space-y-2">
                  <span className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>{copy.rotationLabel}</span>
                    <span className="tabular-nums text-muted-foreground">{settings.rotation}°</span>
                  </span>
                  <input
                    type="range"
                    min={-45}
                    max={45}
                    step={1}
                    value={settings.rotation}
                    onChange={(event) => updateSettings('rotation', Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </label>

                <label className="block space-y-2">
                  <span className="text-sm font-medium">{copy.outputFormat}</span>
                  <select
                    value={settings.outputFormat}
                    onChange={(event) => updateSettings('outputFormat', event.target.value as OutputFormat)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                    <option value="webp">WebP</option>
                  </select>
                </label>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => void handleDownload()}
                  disabled={isProcessing}
                >
                  {isProcessing
                    ? copy.processing
                    : error
                      ? copy.retryDownload
                      : copy.applyButton}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{copy.summary}</h2>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{copy.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(image.file.size)}
                    </strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{copy.originalDimensions}</span>
                    <strong className="font-medium text-foreground">
                      {image.width} x {image.height}
                    </strong>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-lg font-semibold tracking-normal">{copy.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {copy.related.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-lg border bg-card p-4 text-sm shadow-sm transition-colors hover:border-foreground/30 hover:bg-accent/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="font-medium text-foreground">{item.title}</span>
              <span className="mt-2 block leading-6 text-muted-foreground">
                {item.description}
              </span>
            </a>
          ))}
        </div>
      </section>
    </div>
  )
}
